import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, TagOutlined } from '@ant-design/icons';
import { Button, Modal, Tag } from 'antd';
import { getDivisionName, isUserMultiTenant, shouldShowDivision } from '../../../../../swzutils/UserPermissionsUtil';
import HasPermissions from "../../../../../containers/HasPermissions";
import AddressSuburbPopover from "../AddressSuburbPopover";
import ConsignmentFreightPopover from "../ConsignmentFreightPopover";
import TemplateToConsignmentModal from "./TemplateToConsignmentModal";
import axios from "axios";
import { axiosErrorHandler, showSuccessNotification } from "../../../../../swzutils";
import HelpLabel from "../../../components/HelpLabel";
import PermissionDropdownButton from "../../../components/PermissionDropdownButton";
import PaginatedFilterableTable, {} from "../../../components/table/PaginatedFilterableTable";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../../store/slice/auth";
import { useAppSelector } from "../../../../../store";
const ConsignmentsTemplatesTable = () => {
    const tableRef = useRef(null);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createFromTemplate, setCreateFromTemplate] = useState(undefined);
    const [modal, contextHolder] = Modal.useModal();
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const refresh = () => {
        tableRef.current?.fetch();
    };
    const showCreateModal = (template) => {
        setCreateModalVisible(true);
        setCreateFromTemplate(template);
    };
    const hideCreateModal = () => {
        setCreateModalVisible(false);
        setCreateFromTemplate(undefined);
    };
    const confirmDelete = (consignmentTemplate) => {
        modal.confirm({
            title: 'Are you sure?',
            content: 'Delete Template',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => execDelete(consignmentTemplate)
        });
    };
    const execDelete = (consignmentTemplate) => {
        axios.delete(`/admin/v1/consignment-templates/${consignmentTemplate.id}`)
            .then(( /*response*/) => tableRef.current?.removeRecordById(consignmentTemplate.id))
            .catch(axiosErrorHandler);
    };
    const divisionName = getDivisionName(loggedInUser, selectedTenant);
    const columns = [{
            title: 'Profile',
            dataIndex: ['tenant', 'name'],
            sorter: false,
            condition: isUserMultiTenant(loggedInUser),
            width: '10%'
        }, {
            title: divisionName,
            dataIndex: 'division',
            sorter: false,
            width: '10%',
            condition: shouldShowDivision(loggedInUser, selectedTenant),
            filter: {
                type: 'text',
                requestKey: 'divisionNameLike',
                placeholder: divisionName
            },
            render: (text, record) => {
                if (record.division) {
                    return _jsx(Tag, { children: record.division.name }, record.division.id);
                }
                return null;
            }
        }, {
            title: 'Customer',
            key: 'customerName',
            dataIndex: ['customer', 'name'],
            filter: {
                type: 'text',
                requestKey: 'customerNameLike',
                placeholder: 'Search name'
            },
            width: '15%'
        }, {
            title: 'Pickup Location',
            key: 'sender',
            filter: {
                type: 'text',
                requestKey: 'senderCompanyNameLike',
                placeholder: 'Company name'
            },
            width: '15%',
            render: (text, record) => (_jsx(AddressSuburbPopover, { address: record.sender.address, title: record.sender.companyName, isPickup: true })),
        }, {
            title: 'Delivery Location',
            key: 'receiver',
            filter: {
                type: 'text',
                requestKey: 'receiverCompanyNameLike',
                placeholder: 'Company name'
            },
            width: '15%',
            render: (text, record) => (_jsx(AddressSuburbPopover, { address: record.receiver.address, title: record.receiver.companyName, isPickup: false }))
        }, {
            title: 'Freight',
            width: "20%",
            filter: {
                type: 'text',
                requestKey: 'freightDescriptionLike',
                placeholder: 'Search freight'
            },
            render: (text, record) => (_jsx(ConsignmentFreightPopover, { consignment: record, forcePopover: false }))
        }, {
            title: 'Action',
            key: 'action',
            width: "5%",
            render: (text, record) => (_jsx(PermissionDropdownButton, { label: (_jsxs(_Fragment, { children: [_jsx(TagOutlined, {}), " Create Delivery"] })), permissions: ['admin:consignmentTemplate:update'], onClick: () => showCreateModal(record), menuItems: [{
                        key: "app/consignments/templates/update",
                        permissions: ['admin:consignmentTemplate:update'],
                        label: "Edit",
                        icon: _jsx(EditOutlined, {}),
                        route: `#/app/consignments/templates/edit/${record.id}`
                    }, {
                        key: 'app/consignments/templates/delete',
                        permissions: ['admin:consignmentTemplate:delete'],
                        label: 'Delete',
                        icon: _jsx(DeleteOutlined, {}),
                        onClick: () => confirmDelete(record)
                    }] }))
        }];
    return (_jsxs("article", { className: "article swz-fixed-table-article", children: [contextHolder, _jsx("h2", { className: "article-title", children: _jsx(HelpLabel, { title: "Delivery Favourites", iconSize: "large", helpText: "By adding a delivery favourite you can use this to quickly create and edit a new delivery" }) }), _jsxs("div", { className: "table-operations", children: [_jsx(HasPermissions, { permissions: ['admin:consignment:create'], children: _jsx(Button, { href: "#/app/consignments/templates/edit/create", title: "Create", icon: _jsx(PlusOutlined, {}), children: "Add a new Delivery Favourite" }) }), _jsx(Button, { className: "pull-right", onClick: refresh, title: "Refresh", icon: _jsx(ReloadOutlined, {}) })] }), _jsx(PaginatedFilterableTable, { tableKey: 'consignmentTemplatesTable', ref: tableRef, dataUri: "/admin/v1/consignment-templates", columns: columns, refreshOnSelectedTenantChanged: true }), _jsx(TemplateToConsignmentModal, { open: createModalVisible, consignmentTemplate: createFromTemplate, onCancel: hideCreateModal, onCreated: (consignment) => {
                    hideCreateModal();
                    showSuccessNotification(_jsxs("span", { children: ["Delivery ", _jsx("a", { href: `#/app/consignments/view/${consignment.id}`, children: consignment.consignmentRef }), " Created"] }));
                } })] }));
};
export default ConsignmentsTemplatesTable;
